import { useState, useEffect } from "react";
import { Head, router } from "@inertiajs/react";
import classNames from "classnames";

import projectPreviewFallbackImage from "/assets/project-preview-fallback.jpg";
import searchIcon from "/assets/search.svg";

import Layout from "/AdminLayout";
import { useToast } from "/utils/toast-context.jsx";
import Modal from "/components/Modal";
import Button from "/components/Button";
import SortableColumnHead from "/components/Admin/SortableColumnHead";
import DebouncedSearch from "/components/Admin/DebouncedSearch";
import SettingTextField from "/components/Admin/SettingTextField";

const ProjectIndex = ({
  projects,
  project_visibilities,
  sorting_params,
  filter_params,
  projects_per_page,
  dictionaries,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState();
  const [search, setSearch] = useState("");

  const { successToast, errorToast } = useToast();

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setProjectToDelete(null);
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
  };

  const deleteProject = async () => {
    const projectId = projectToDelete.id;
    await router.delete(`/admin/projects/${projectId}`);
    setIsDeleteModalOpen(false);
  };

  const sortingStatus = (field) => {
    if (sorting_params[0] !== field)
      return null;

    return sorting_params[1];
  };

  const onSearch = (value) => {
    if (value == search)
      return;

    setSearch(value);
    const q = value && value.trim();

    const sortingPart = {};
    if (sorting_params)
      sortingPart.sort_by = `${sorting_params[0]} ${sorting_params[1]}`;

    router.get(
      '/admin/projects',
      { ...filter_params, ...sortingPart, q },
      {
        preserveState: true,
        preserveScroll: true,
      }
    )
  };

  const onSorting = (field) => (dir) => {
    router.get(
      '/admin/projects',
      { ...filter_params, sort_by: `${field} ${dir}` }
    )
  };

  const onPublish = async (projectId) => {
    router.put(
      `/admin/projects/${projectId}`,
      { state: "publish" },
      {
        onSuccess: () => successToast("Проект опубликован"),
        onError: () => errorToast("Неожиданная ошибка"),
      },
    );
  };

  const draftProjects = projects.filter(p => p.draft);
  const undraftedProjects = projects.filter(p => !p.draft);

  return (
    <>
      <Head>
        <title>Проекты</title>
      </Head>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        className="modal--autowidth"
      >
        <div className="modal__text">
          Вы уверены, что хотите<br />удалить этот проект?
        </div>

        <div className="modal__actions">
          <Button onClick={deleteProject} className="btn btn--dark">Удалить</Button>
          <Button onClick={handleCloseDeleteModal}>Отменить</Button>
        </div>
      </Modal>

      <div className="table-header">
        <div className="table-header__left">
          <h2>Проекты</h2>
          <div className="pagination-amount">
            <span>Выдача проектов на главной</span> <span>по</span> <SettingTextField name="projects_per_page" value={projects_per_page} /> <span>шт</span>
          </div>
        </div>

        <div className="table-header__right">
          <div className="search">
            <DebouncedSearch
              defaultValue={filter_params.q}
              icon={<img src={searchIcon} />}
              placeholder="найти"
              onChange={onSearch}
            />
          </div>

          <a href="/admin/projects/new" className="btn">Добавить проект</a>
        </div>
      </div>

      <table className="tbl">
        <thead>
          <tr>
            <th>Привью</th>
            <SortableColumnHead status={sortingStatus('title_ru')} onClick={onSorting('title_ru')}>Название кейса</SortableColumnHead>
            <SortableColumnHead status={sortingStatus('client_id')} onClick={onSorting('client_id')}>{dictionaries.clients.name_ru}</SortableColumnHead>
            <SortableColumnHead status={sortingStatus('visibility')} onClick={onSorting('visibility')}>Видимость</SortableColumnHead>
            <SortableColumnHead status={sortingStatus('dirty')} onClick={onSorting('dirty')}>Статус</SortableColumnHead>
            <SortableColumnHead status={sortingStatus('order')} onClick={onSorting('order')}>№</SortableColumnHead>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {draftProjects.map((project, index) => (
            <tr key={project.id} className={classNames({ "tbl-last-draft": !filter_params.draft && (index >= draftProjects.length - 1) })}>
              <td className="tbl-preview"><img src={project.og_image_ru?.url || projectPreviewFallbackImage} /></td>
              <td>{project.title_ru}</td>
              <td>{project.client?.name_ru}</td>
              <td>{project_visibilities[project.visibility]}</td>
              <td>Черновик</td>
              <td>—</td>
              <td className="tbl-extra">
                <div className="tbl-actions tbl-actions--hiding">
                  <div className="flex-row gap-3">
                    <a href={`/admin/projects/${project.id}/edit`} className="lnk lnk--blue">Редактировать</a>
                    <Button onClick={() => onPublish(project.id)} className="lnk lnk--blue">Опубликовать</Button>
                  </div>

                  <div className="flex-row">
                    <Button className="lnk lnk--red" onClick={() => handleDeleteClick(project)}>Удалить</Button>
                  </div>
                </div>
              </td>
            </tr>
          ))}

          {undraftedProjects.map((project) => (
            <tr key={project.id}>
              <td className="tbl-preview"><img src={project.og_image_ru?.url || projectPreviewFallbackImage} /></td>
              <td>{project.title_ru}</td>
              <td>{project.client?.name_ru}</td>
              <td>{project_visibilities[project.visibility]}</td>
              <td>{project.dirty ? "Изменен" : "Опубликован"}</td>
              <td>{project.order ?? '—'}</td>
              <td className="tbl-extra">
                <div className="tbl-actions tbl-actions--hiding">
                  <div className="flex-row gap-3">
                    <a href={`/admin/projects/${project.id}/edit`} className="lnk lnk--blue">Редактировать</a>
                    {project.dirty && (
                      <Button onClick={() => onPublish(project.id)} className="lnk lnk--blue">Опубликовать</Button>
                    )}
                  </div>

                  <div className="flex-row">
                    <Button className="lnk lnk--red" onClick={() => handleDeleteClick(project)}>Удалить</Button>
                  </div>
                </div>

                {project.dirty && (
                  <div className="tbl-note">
                    Последние изменения не опубликованы
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

ProjectIndex.layout = (page) => <Layout children={page} />

export default ProjectIndex;
